import {Utils, ArgValidator, ApiClientCommunication} from 'neo-common';

import {InitRequest} from './Requests/InitRequest';
import {CollectRequest} from './Requests/CollectRequest';
import {BalanceRequest} from './Requests/BalanceRequest';
import {InitResponse} from './Responses/InitResponse';
import {GameResponse} from './Responses/GameResponse';
import {BalanceResponse} from './Responses/BalanceResponse';
import {SpinRequest} from './Requests/SpinRequest';
import {FreeSpinRequest} from './Requests/FreeSpinRequest';
import {RespinRequest} from './Requests/RespinRequest';
import {StartRequest} from './Requests/StartRequest';
import {GambleSelectRequest} from './Requests/GambleSelectRequest';
import {GambleHalveRequest} from './Requests/GambleHalveRequest';
import {PickSelectRequest} from './Requests/PickSelectRequest';
import {Request} from './Requests/Request';
import {EnterGameRequest} from './Requests/EnterGameRequest';
import {LeaveGameRequest} from './Requests/LeaveGameRequest';
import {GameModuleBaseResponse} from './Responses/GameModuleBaseResponse';
import {GameModuleGambleResponse} from './Responses/GameModuleGambleResponse';
import {GameModuleFreespinResponse} from './Responses/GameModuleFreespinResponse';
import {GameModulePickResponse} from './Responses/GameModulePickResponse';
import {GameModuleRouletteResponse} from './Responses/GameModuleRouletteResponse';
import {EnterGameResponse} from './Responses/EnterGameResponse';
import {LeaveGameResponse} from './Responses/LeaveGameResponse';
import {GameClientOptions} from './GameClientOptions';
import {GameModuleResponseFactory} from './Responses/GameModuleResponseFactory';
import {EndRequest} from './Requests/EndRequest';

import {GameModules} from './GameModules';
import {SpinBet} from './Types/SpinBet';
import {Color} from './Types/Color';
import {Suit} from './Types/Suit';
import {GameClientInterceptorFactory} from './GameClientInterceptorFactory';
import {ConfigurationRequest} from './Requests/ConfigurationRequest';
import {ConfigurationResponse} from './Responses/ConfigurationResponse';
import {InterceptorRequestContext} from './IGameClientInterceptor';
import {RealityCheckRequest} from './Requests/RealityCheckRequest';
import {RealityCheckResponse} from './Responses/RealityCheckResponse';
import {FreeRoundsOffersResponse} from './Responses/FreeRoundsOffersResponse';
import {FreeRoundsOffersRequest} from './Requests/FreeRoundsOffersRequest';
import {FreeRoundsActivateOfferRequest} from './Requests/FreeRoundsActivateOfferRequest';
import {FreeRoundsActivateOfferResponse} from './Responses/FreeRoundsActivateOfferResponse';
import {FreeRoundsDeclineOfferResponse} from './Responses/FreeRoundsDeclineOfferResponse';
import {FreeRoundsDeclineOfferRequest} from './Requests/FreeRoundsDeclineOfferRequest';
import {ServerTimeResponse} from './Responses/ServerTimeResponse';
import {ServerTimeRequest} from './Requests/ServerTimeRequest';
import {RealityCheckGetInfoResponse} from './Responses/RealityCheckGetInfoResponse';
import {RealityCheckGetInfoRequest} from './Requests/RealityCheckGetInfoRequest';
import {ProgressiveBonusGetInfoResponse} from './Responses/ProgressiveBonusGetInfoResponse';
import {ProgressiveBonusGetInfoRequest} from './Requests/ProgressiveBonusGetInfoRequest';
import {MaxPowerBonusInitResponse} from './Responses/MaxPowerBonusInitResponse';
import {MaxPowerBonusInitRequest} from './Requests/MaxPowerBonusInitRequest';
import {MaxPowerBonusSpinResponse} from './Responses/MaxPowerBonusSpinResponse';
import {MaxPowerBonusSpinRequest} from './Requests/MaxPowerBonusSpinRequest';
import {RouletteSpinBet} from './Types/RouletteSpinBet';
import {RouletteSpinRequest} from './Requests/RouletteSpinRequest';
import {GameModuleBonusRespinsResponse} from './Responses/GameModuleBonusRespinsResponse';
import {BonusRespinsSpinRequest} from './Requests/BonusRespinsSpinRequest';
import {PlayerConfigurationResponse} from './Responses/PlayerConfigurationResponse';
import {PlayerConfigurationRequest} from './Requests/PlayerConfigurationRequest';
import {PlayerLogErrorResponse} from './Responses/PlayerLogErrorResponse';
import {PlayerLogErrorRequest} from './Requests/PlayerLogErrorRequest';
import {BuyFeatureRequest} from './Requests/BuyFeatureRequest';
import {PlayerLogEventsResponse} from './Responses/PlayerLogEventsResponse';
import {PlayerLogEventsRequest} from './Requests/PlayerLogEventsRequest';
import {BlackJackDealRequest} from './Requests/BlackJackDealRequest';
import {BlackJackHitRequest} from './Requests/BlackJackHitRequest';
import {BlackJackStandRequest} from './Requests/BlackJackStandRequest';
import {BlackJackDealBet} from './Types/BlackJackDealBet';
import {GameModuleBlackJackResponse} from './Responses/GameModuleBlackJackResponse';

const balanceRouteUrl = 'player/balance';

const playerConfigureRouteUrl = 'player/configure';

const playerLogErrorUrl = 'player/logError';

const playerLogEventsUrl = 'player/LogEvents';

const realityCheckRouteUrls = {
    close: 'player/closeRealityCheck',
    reset: 'player/resetRealityCheck',
    getInfo: 'player/getRealityCheckInfo'
};

const freeRoundBonusUrls = {
    getOffer: 'FreeRounds/GetCurrentOffer',
    activateOffer: 'FreeRounds/ActivateOffer',
    declineOffer: 'FreeRounds/DeclineOffer'
};

const maxPowerBonusUrls = {
    init: 'ProgressiveBonus/MaxPowerBonus/Init',
    spin: 'ProgressiveBonus/MaxPowerBonus/Spin'
};

const serverTime = 'ServerTime';

declare var require: any;

export class MessageIdGenerator {
    public static generateId(): number {
        return new Date().getTime();
    }
}

/**
 * @class GameClient - implementation of game client build on top of @ApiClient class.
 * @summary This class has default implementation of common requests like "Init", "Collect", etc.
 * Any descendant of this class should add its own requests and handling of errors.
 */
export class GameClient extends ApiClientCommunication.ApiClient {
    private _routeUrl: string;
    private _offerId: number = null;
    private _bonusId: number = -1;
    private _autoplay: boolean = false;
    protected lastCfgVersion: number;

    /**
     * @constructor - creates new game client
     * @param gameKey - key for game module
     * @param options - options for api client
     */
    constructor(gameKey: string, options: GameClientOptions) {
        super(gameKey, options);

        ArgValidator.ensureStringIsNotNullOrEmpty(gameKey, 'gameKey');
        ArgValidator.ensureStringIsNotNullOrEmpty(options.jwtToken, 'options.jwtToken');

        this._gameKey = gameKey;
        this._routeUrl = `/game/${gameKey}/`;

        this.lastCfgVersion = null;
    }

    /**
     * @method sendKeepAliveRequest - sends keep alive request to server to keep server session, ticket, etc alive
     * event when api client is inactive with server due some time (defined in @ApiClientOptions.keepAliveTime)
     * @summary for @GameClient class, it sends 'balance' request as keep alive request.
     */
    protected sendKeepAliveRequest(): Promise<ApiClientCommunication.ResponseBase> {
        let error: Error = null;

        return this.sendRequest<BalanceResponse>(new BalanceRequest(),
            ApiClientCommunication.ApiClientConsts.HttpMethod.GET, balanceRouteUrl, {
                supressHandleError: true,
                overwriteRetryCount: 0,
                customHandleError: (clientError) => {
                    if (!Utils.isNullOrUndefined(clientError.httpStatusCode)) {
                        if (clientError.httpStatusCode === 401) { //session expired
                            const err = Utils.isNullOrUndefined(clientError.error) ? null : clientError.error.message;
                            error = new Error(err || clientError.httpStatusCode.toString());
                        }
                    }
                }
            })
            .finally(() => {
            if (!Utils.isNullOrUndefined(error)) {
                throw error;
            }
        });
    }

    /**
     * @method setOfferId - set offer id from client for free round bonus campaing
     */
    public setOfferId(id: number): void
    {
        this._offerId = id;
    }

    /**
     * @method setBonusId - set bonus id from client for progressive bonus campaing
     */
    public setBonusId(id: number): void
    {
        this._bonusId = id;
    }

    /**
     * @method setAutoplay - set information about autoplay
     */
    public setAutoplay(autoplay: boolean): void
    {
        this._autoplay = autoplay;
    }

    /**
     * @method sendConfigurationRequest - sends configuration request to server to get actual configuration for game
     */
    public sendConfigurationRequest(version: string): Promise<ConfigurationResponse> {
        const versionQuery = version === '' ? '' : `?version=${version}`;
        const routeUrl = `configuration/${this._gameKey}${versionQuery}`;

        return this.sendRequest<ConfigurationResponse>(new ConfigurationRequest(),
            ApiClientCommunication.ApiClientConsts.HttpMethod.GET, routeUrl, {
                supressHandleError: false,
                overwriteRetryCount: 0
            });
    }

    /**
     * @method sendPlayerConfigurationRequest - sends player configuration request to server to update player configuration
     */
    public sendPlayerConfigurationRequest(data: any): Promise<PlayerConfigurationResponse> {
        return this.sendRequest<PlayerConfigurationResponse>(new PlayerConfigurationRequest(data),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST, playerConfigureRouteUrl, {
                supressHandleError: true,
                overwriteRetryCount: 0
            });
    }

    /**
     * @method sendPlayerLogError - sends player errors to logz.io platform to analyze them
     */
    public sendPlayerLogError(message: string, customData?: any): Promise<PlayerLogErrorResponse> {
        return this.sendRequest<PlayerLogErrorResponse>(new PlayerLogErrorRequest(message, customData),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST, playerLogErrorUrl, {
                supressHandleError: true,
                overwriteRetryCount: 0
            });
    }

    /**
     * @method sendPlayerLogEvents - sends player game session log events
     */
    public sendPlayerLogEvents(data: any): Promise<PlayerLogEventsResponse> {
        return this.sendRequest<PlayerLogEventsResponse>(new PlayerLogEventsRequest(data),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST, playerLogEventsUrl, {
                supressHandleError: true,
                overwriteRetryCount: 0
            });
    }

    /**
     * @method sendServerTimerRequest - get actual server time from backend
     */
    public sendServerTimerRequest(): Promise<ServerTimeResponse> {
        return this.sendRequest<ServerTimeResponse>(new ServerTimeRequest(),
            ApiClientCommunication.ApiClientConsts.HttpMethod.GET, serverTime, {
                supressHandleError: true,
                overwriteRetryCount: 0
            });
    }

    /**
     * @method sendFreeRoundsOffersRequest - sends get free rounds request for actual information about free rounds for game
     */
    public sendFreeRoundsOffersRequest(gameCode: string): Promise<FreeRoundsOffersResponse> {
        let error: Error = null;
        return this.sendRequest<FreeRoundsOffersResponse>(new FreeRoundsOffersRequest(gameCode),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST, freeRoundBonusUrls.getOffer, {
                supressHandleError: false,
                overwriteRetryCount: 0,
                customHandleError: (clientError) => {
                    const err = Utils.isNullOrUndefined(clientError.error) ? null : clientError.error.message;
                    error = new Error(err || clientError.httpStatusCode.toString());
                }
            }).finally(() => {
            if (error) {
                throw error;
            }
        });
    }

    /**
     * @method sendFreeRoundsActivateOfferRequest - send free round bonus activate request for selected offerId
     */
    public sendFreeRoundsActivateOfferRequest(gameCode: string, offerId: number): Promise<FreeRoundsActivateOfferResponse> {
        let error: Error = null;
        return this.sendRequest<FreeRoundsActivateOfferResponse>(new FreeRoundsActivateOfferRequest(gameCode, offerId),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST, freeRoundBonusUrls.activateOffer, {
                supressHandleError: false,
                overwriteRetryCount: 3,
                customHandleError: (clientError) => {
                    const err = Utils.isNullOrUndefined(clientError.error) ? null : clientError.error.message;
                    error = new Error(err || clientError.httpStatusCode.toString());
                }
            }).finally(() => {
            if (error) {
                throw error;
            }
        });
    }

    /**
     * @method sendFreeRoundsDeclineOfferRequest - send free round bonus decline request for selected offerId
     */
    public sendFreeRoundsDeclineOfferRequest(gameCode: string, offerId: number): Promise<FreeRoundsDeclineOfferResponse> {
        return this.sendRequest<FreeRoundsDeclineOfferResponse>(new FreeRoundsDeclineOfferRequest(gameCode, offerId),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST, freeRoundBonusUrls.declineOffer, {
                supressHandleError: true,
                overwriteRetryCount: 0
            });
    }

    /**
     * @method sendProgressiveBonusGetInfoRequest - send progressive bonus get info request for selected bonusId
     */
    public sendProgressiveBonusGetInfoRequest(bonusId: number): Promise<ProgressiveBonusGetInfoResponse> {
        let error: Error = null;

        return this.sendRequest<ProgressiveBonusGetInfoResponse>(new ProgressiveBonusGetInfoRequest(bonusId),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST,  `ProgressiveBonus/${this._gameKey}/GetInfo`, {
                supressHandleError: true,
                overwriteRetryCount: 3,
                customHandleError: (clientError) => {
                    if (!Utils.isNullOrUndefined(clientError.httpStatusCode)) {
                        if (clientError.httpStatusCode === 401) { //session expired
                            const err = Utils.isNullOrUndefined(clientError.error) ? null : clientError.error.message;
                            error = new Error(err || clientError.httpStatusCode.toString());
                        }
                    }
                }
            })
            .finally(() => {
                if (!Utils.isNullOrUndefined(error)) {
                    throw error;
                }
            });
    }

    /**
     * @method MaxPowerBonusInit - send max power bonus init request
     */
    public sendMaxPowerBonusInitRequest(ticket: {id: string} = null): Promise<MaxPowerBonusInitResponse> {
        return this.sendRequest<MaxPowerBonusInitResponse>(new MaxPowerBonusInitRequest(ticket),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST,  maxPowerBonusUrls.init, {
                supressHandleError: true,
                overwriteRetryCount: 0
            });
    }

    /**
     * @method MaxPowerBonusSpin - send max power bonus spin request
     */
    public sendMaxPowerBonusSpinRequest(ticket: {id: string} = null): Promise<MaxPowerBonusSpinResponse> {
        return this.sendRequest<MaxPowerBonusSpinResponse>(new MaxPowerBonusSpinRequest(ticket),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST,  maxPowerBonusUrls.spin);
    }

    /**
     * sends request to server
     * @param request - request object as JSON to be send to api server
     * @param module - name of module (eg: root, base, gamble, ...)
     * @typedef TResponse - Type for expected response json object.
     * @returns Promise<TResponse> - promise with response of type @Response or descendats of @Response type.
     */
    protected sendGameRequest<TResponse extends ApiClientCommunication.ResponseBase>(request: Request<TResponse>,
        module: string): Promise<TResponse> {
        return this.internalSendGameRequest(request as any, module) as Promise<TResponse>;
    }

    /**
     * sends request to server
     * @param request - request object as JSON to be send to api server
     * @param module - name of module (eg: root, base, gamble, ...)
     * @typedef TResponse - Type for expected response json object.
     * @returns Promise<TResponse> - promise with response of type @Response or descendats of @Response type.
     */
    protected internalSendGameRequest<TResponse extends ApiClientCommunication.ResponseBase>(request: Request<TResponse>,
        module: string): Promise<TResponse> {

        const requestContext: InterceptorRequestContext = {
            request: request,
            module: module,
            context: {}
        };

        return GameClientInterceptorFactory.onSendGameRequest(requestContext, this.sendGameRequest.bind(this))
            .then(() => {
                ArgValidator.ensureIsNotNullOrUndefined(request.messageId, 'request.messageId');
                ArgValidator.ensureStringIsNotNullOrEmpty(module, 'module');
                ArgValidator.ensureIsNotNullOrUndefined(request.actionName, 'request.actionName');

                const action = request.actionName();
                ArgValidator.ensureStringIsNotNullOrEmpty(action, 'action');

                if (!Utils.isNullOrUndefined(this.lastCfgVersion)) {
                    request.cfgVersion = this.lastCfgVersion;
                }

                let params = '';
                let paramsArr:Array<string> = [];
                if(this._offerId !== null) {
                    paramsArr.push('offerId=' + this._offerId);
                }
                if(this._bonusId > 0) {
                    paramsArr.push('bonusId=' + this._bonusId);
                }
                if(this._autoplay) {
                    paramsArr.push('autoplay=true');
                }

                if(paramsArr.length > 0) {
                    params = '?';
                    for(let i: number = 0; i < paramsArr.length; i++) {
                        if(i === 0) {
                            params += paramsArr[i];
                        } else {
                            params += '&' + paramsArr[i];
                        }
                    }
                }

                const cfgVer = 'with cfgVersion: ' + (Utils.valueOrDefault(request.cfgVersion, '-'));
                const logHeader = `sending request action "${action}" to module "${module}" ${cfgVer} at base url: ${this._apiUrl} ...`;
                const routeUrl = `game/${this._gameKey}/${module}/${action}` + params;

                const sendOptions: ApiClientCommunication.ApiClientSendRequestOptions = {
                    logHeader: logHeader,
                    customHandleError: (clientError => {
                        GameClientInterceptorFactory.onRequestError(requestContext, clientError);
                    })
                };

                return this.sendRequest(request, ApiClientCommunication.ApiClientConsts.HttpMethod.POST, routeUrl, sendOptions)
                    .then(response => {

                        // if response is of type 'GameResponse' look for 'cfgVersion' property
                        // and update field 'lastCfgVersion' only when if not already set -> Fix of WI_3880
                        if (response instanceof GameResponse && Utils.isNullOrUndefined(this.lastCfgVersion)) {
                            this.lastCfgVersion = (response as GameResponse).cfgVersion;
                        }

                        if (Utils.isNullOrUndefined(response)) {
                            return response;
                        }

                        return GameClientInterceptorFactory.onResponseReceived(requestContext, response)
                            .then(() => {
                                return response;
                            });
                    });
            })
            .finally(() => {
                GameClientInterceptorFactory.onSendGameRequestFinal(requestContext);
            });
    }

    public balance(): Promise<BalanceResponse> {
        return this.sendRequest<BalanceResponse>(new BalanceRequest(),
            ApiClientCommunication.ApiClientConsts.HttpMethod.GET, balanceRouteUrl);
    }

    public closeRealityCheck(): Promise<RealityCheckResponse> {
        let error: Error = null;

        return this.sendRequest<RealityCheckResponse>(new RealityCheckRequest(),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST, realityCheckRouteUrls.close, {
                customHandleError: (clientError) => {
                    const err = Utils.isNullOrUndefined(clientError.error) ? null : clientError.error.message;
                    error = new Error(err || clientError.httpStatusCode.toString());
                }
            })
            .finally(() => {
                if (error) {
                    throw error;
                }
            });
    }

    public resetRealityCheck(interval?: number): Promise<RealityCheckResponse> {
        let error: Error = null;

        return this.sendRequest<RealityCheckResponse>(new RealityCheckRequest(interval),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST, realityCheckRouteUrls.reset, {
                customHandleError: (clientError) => {
                    const err = Utils.isNullOrUndefined(clientError.error) ? null : clientError.error.message;
                    error = new Error(err || clientError.httpStatusCode.toString());
                }
            })
            .finally(() => {
                if (error) {
                    throw error;
                }
            });
    }

    public getRealityCheckInfo(): Promise<RealityCheckGetInfoResponse> {
        return this.sendRequest<RealityCheckGetInfoResponse>(new RealityCheckGetInfoRequest(),
            ApiClientCommunication.ApiClientConsts.HttpMethod.POST, realityCheckRouteUrls.getInfo, {
                supressHandleError: true,
                overwriteRetryCount: 0
            });
    }

    /**
     * @method init - send "init" request to api server.
     * @param customData - optional custom data for request.
     * @returns promise with response as @InitResponse type.
     */
    public init(customData?: any): Promise<InitResponse> {
        const request = new InitRequest(customData);

        return this.sendGameRequest<InitResponse>(request, GameModules.Root);
    }

    /**
     * @method collect - send "collect" request to api server.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public collect(customData?: any): Promise<GameResponse> {
        const request = new CollectRequest(customData);

        return this.sendGameRequest<GameResponse>(request, GameModules.Root);
    }

    /**
     * @method enterGame - send "enter" request to api server.
     * Enter game request is used to inform server, that client wants play a game.
     * @param customData - optional custom data for request.
     * @returns promise with response as @EnterGameResponse type.
     */
    public enterGame(customData?: any): Promise<EnterGameResponse> {
        const request = new EnterGameRequest(customData);

        return this.sendGameRequest<EnterGameResponse>(request, GameModules.Root);
    }

    /**
     * @method leaveGame - send "leave" request to api server.
     * Leave game request is used by client when leaving game and provide option for server to optionally end the active game round.
     * @param customData - optional custom data for request.
     * @returns promise with response as @LeaveGameResponse type.
     */
    public leaveGame(customData?: any): Promise<LeaveGameResponse> {
        const request = new LeaveGameRequest(customData);

        return this.sendGameRequest<LeaveGameResponse>(request, GameModules.Root);
    }

    /**
     * @method spin - send "spin" request to api server.
     * @param module - module name where to send "spin" request.
     * @param bet - bet information.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public spin(module: string, bet: SpinBet, customData?: any): Promise<GameResponse> {
        ArgValidator.ensureStringIsNotNullOrEmpty(module, 'module');
        ArgValidator.ensureIsNotNullOrUndefined(bet, 'bet');

        const request = new SpinRequest(bet, customData);
        return this.sendGameRequest<GameResponse>(request, module);
    }

    /**
     * @method rouletteSpin - send "spin" request to api server.
     * @param module - module name where to send "spin" request.
     * @param bets - list of bets placed for one spin.
     * @param lastChip - last chip selected by player.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public rouletteSpin(module: string, bets: Array<RouletteSpinBet>, lastChip: number, customData?: any): Promise<GameResponse> {
        ArgValidator.ensureStringIsNotNullOrEmpty(module, 'module');
        ArgValidator.ensureIsNotNullOrUndefined(bets, 'bets');

        const request = new RouletteSpinRequest(bets, lastChip, customData);
        return this.sendGameRequest<GameResponse>(request, module);
    }

    /**
     * @method freespin - send "freespin" request to api server.
     * @param module - module name where to send "spin" request.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public freespin(module: string, customData?: any): Promise<GameResponse> {
        ArgValidator.ensureStringIsNotNullOrEmpty(module, 'module');

        const request = new FreeSpinRequest(customData);
        return this.sendGameRequest<GameResponse>(request, module);
    }

    /**
     * @method endFreespin - send "end" request to module 'freespins' to api server.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public endFreespin(customData?: any): Promise<GameResponse> {
        return this.end(GameModules.Freespins, customData);
    }

    /**
     * @method bonusRespinsSpin - send bonusrespins spin request to api server.
     * @param module - module name where to send "spin" request.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public bonusRespinsSpin(module: string, customData?: any): Promise<GameResponse> {
        ArgValidator.ensureStringIsNotNullOrEmpty(module, 'module');

        const request = new BonusRespinsSpinRequest(customData);
        return this.sendGameRequest<GameResponse>(request, module);
    }

    /**
     * @method bonusStart - send bonus "start" request to api server.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public bonusRespinsStart(customData?: any): Promise<GameResponse> {
        return this.start(GameModules.BonusRespins, customData);
    }

    /**
     * @method endBonus - send "end" request to module 'bonus' to api server.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public endBonusRespins(customData?: any): Promise<GameResponse> {
        return this.end(GameModules.BonusRespins, customData);
    }

    /**
     * @method endPick - send "end" request to module 'pick' api server.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public endPick(customData?: any): Promise<GameResponse> {
        return this.end(GameModules.Pick, customData);
    }

    /**
     * @method end - send "end" request to api server.
     * @param module - module name where to send "end" request.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public end(module: string, customData?: any): Promise<GameResponse> {
        const request = new EndRequest(customData);
        return this.sendGameRequest<GameResponse>(request, module);
    }

    /**
     * @method spin - send "start" request to api server.
     * @param module - module name where to send "start" request.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public start(module: string, customData?: any): Promise<GameResponse> {
        ArgValidator.ensureStringIsNotNullOrEmpty(module, 'module');

        const request = new StartRequest(customData);
        return this.sendGameRequest<GameResponse>(request, module);
    }

    /**
     * @method spin - send pick "start" request to api server.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public pickStart(customData?: any): Promise<GameResponse> {
        return this.start(GameModules.Pick, customData);
    }

    /**
     * @method respin - send "respin" request to api server.
     * @param module - module name where to send "respin" request.
     * @param reels - reels information.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public respin(module: string, reels: number, customData?: any): Promise<GameResponse> {
        ArgValidator.ensureStringIsNotNullOrEmpty(module, 'module');
        ArgValidator.ensureIsNumber(reels, 'reels');

        const request = new RespinRequest(reels, customData);
        return this.sendGameRequest<GameResponse>(request, module);
    }

    /**
     * @method spin - send gamble select request to api server.
     * @param color - selected color
     * @param suit - selected suit
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public gambleSelect(color?: Color, suit?: Suit, customData?: any): Promise<GameResponse> {
        const request = new GambleSelectRequest(color, suit, customData);
        return this.sendGameRequest<GameResponse>(request, GameModules.Gamble);
    }

    /**
     * @method gambleHalve - send gamble halve request to api server.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public gambleHalve(customData?: any): Promise<GameResponse> {
        const request = new GambleHalveRequest(customData);
        return this.sendGameRequest<GameResponse>(request, GameModules.Gamble);
    }

    /**
     * Sends pick select request to api server.
     * @param spot - Identifier of selected spot.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public pickSelect(spot: number, customData?: any): Promise<GameResponse> {
        const request = new PickSelectRequest(spot, customData);
        return this.sendGameRequest<GameResponse>(request, GameModules.Pick);
    }

    /**
     * @method spin - send "spin" request to api server.
     * @param module - module name where to send "spin" request.
     * @param bet - total bet for buy feature.
     * @param originalBet - original bet information.
     * @param buyFeatureName - buy feature name.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public buyFeature(module: string, bet: SpinBet, originalBet: SpinBet, buyFeatureName: string, customData?: any): Promise<GameResponse> {
        ArgValidator.ensureStringIsNotNullOrEmpty(module, 'module');
        ArgValidator.ensureIsNotNullOrUndefined(bet, 'bet');

        const request = new BuyFeatureRequest(bet, originalBet, buyFeatureName, customData);
        return this.sendGameRequest<GameResponse>(request, module);
    }

    /**
     * @method blackJackDeal - send "spin" request to api server.
     * @param bets - list of bets placed for one spin.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public blackJackDeal(bets: Array<BlackJackDealBet>, customData?: any): Promise<GameResponse> {
        ArgValidator.ensureIsNotNullOrUndefined(bets, 'bets');

        const request = new BlackJackDealRequest(bets, customData);
        return this.sendGameRequest<GameResponse>(request, GameModules.Base);
    }

    /**
     * @method blackJackHit - send "spin" request to api server.
     * @param hand - selected hand by player.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public blackJackHit(hand: number, customData?: any): Promise<GameResponse> {
        ArgValidator.ensureIsNumber(hand, 'hand');

        const request = new BlackJackHitRequest(hand, customData);
        return this.sendGameRequest<GameResponse>(request, GameModules.Base);
    }

    /**
     * @method blackJackStand - send "spin" request to api server.
     * @param hand - selected hand by player.
     * @param customData - optional custom data for request.
     * @returns promise with response as @GameResponse type.
     */
    public blackJackStand(hand: number, customData?: any): Promise<GameResponse> {
        ArgValidator.ensureIsNumber(hand, 'hand');

        const request = new BlackJackStandRequest(hand, customData);
        return this.sendGameRequest<GameResponse>(request, GameModules.Base);
    }
}

// =======================================
// register all game module responses
// =======================================

GameModuleResponseFactory.register(GameModuleBaseResponse);
GameModuleResponseFactory.register(GameModuleGambleResponse);
GameModuleResponseFactory.register(GameModuleFreespinResponse);
GameModuleResponseFactory.register(GameModulePickResponse);
GameModuleResponseFactory.register(GameModuleRouletteResponse);
GameModuleResponseFactory.register(GameModuleBlackJackResponse);
GameModuleResponseFactory.register(GameModuleBonusRespinsResponse);


/** WEBPACK FOOTER **
 ** ./Engine/Communication/Game/GameClient.ts
 **/